import { Component, DoCheck, Input, inject } from '@angular/core';
import { AufnahmePunktDocument, Baumart, bodenformString, isB, isG, isM, klimastufeAnzeige, roundToNumber, punktartBeschreibung } from '../../../data-model';
import { EvaluationService } from '../../evaluation.service';
import { ValidationService } from '../../validation.service';
import { DataService } from 'src/app/data.service';
import { AuthService } from 'src/app/auth.service';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-allgemein',
    templateUrl: './bohrpunkt-allgemein.component.html',
    styleUrls: ['../hl-table.component.css'],

})
export class BohrpunktAllgemeinComponent implements DoCheck {

  @Input() bp!: AufnahmePunktDocument;
  @Input() printView!: boolean;

  roundToNumber = roundToNumber;
  aus = inject(AuthService);

  private ds = inject(DataService);
  private es = inject(EvaluationService);
  private vs = inject(ValidationService);

  /** Punktart-Beschreibung (Single Source aus der Punktarten-Liste) zur Anzeige. */
  typName(): string {
    return punktartBeschreibung(this.bp.type);
  }

  /** Prüft ob Kartierer/Datum angezeigt werden darf (nicht für Gäste und nicht-angemeldete) */
  canSeePersonalData(): boolean {
    return this.aus.isAuthenticated.getValue() && !this.aus.isGuest();
  }

  ngDoCheck(): void {
    this.es.setContext(this.bp);
    this.vs.setContext(this.bp);
  }

  baumarten(): string {
    if (typeof this.bp.extendedHeader.baumarten === 'string') return this.bp.extendedHeader.baumarten;

    const baumarten = this.bp.extendedHeader.baumarten as Baumart[];
    let bas = '';
    for (const ba of baumarten) bas += ba.art + '';

    return bas;
  }

  adresse(): string {
    const a = this.bp.header.adresse;
    return [a.abteilung, a.unterabteilung, a.teilflaeche].filter(v => !!v).join(' ');
  }

  gemarkungFlurFS(): string {
    const a = this.bp.header.adresse;
    return [a.gemarkung, a.flur, a.flurstueck].filter(v => v !== undefined && v !== null && v !== '').join(' / ');
  }

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
  }

  geologieDisplay(): string {
    const g = this.bp.extendedHeader?.geologie;
    if (!g) return '';
    const parts: string[] = [];
    if (g.oben) parts.push(g.oben);
    if (g.trenner) parts.push(g.trenner);
    if (g.trenner && g.unten) parts.push(g.unten);
    return parts.join(' ');
  }

  /** Klimastufe: der von Hand eingetragene Wert geht dem automatisch geholten vor. */
  klimastufeDisplay(): string {
    return klimastufeAnzeige(this.bp.extendedHeader?.klimaStufe);
  }

  reliefMoorDisplay(): string {
    const r = this.bp.extendedHeader?.relief;
    return [r?.reliefBestand, r?.reliefBodenvegetation].filter(v => !!v).join(' / ');
  }

  morphologieDisplay(): string {
    const m = this.bp.extendedHeader?.morphologie;
    if (!m) return '';
    return m.filter(v => !!v).join('/');
  }

  hasGemarkung(): boolean {
    return !!this.bp.header.adresse.gemarkung || !!this.bp.header.adresse.flur || !!this.bp.header.adresse.flurstueck;
  }

  isB(): boolean {
    return isB(this.bp);
  }

  isG(): boolean {
    return isG(this.bp);
  }

  isM(): boolean {
    return isM(this.bp);
  }

  /** AltNeu<string> als "neu / alt" anzeigen (alt = klassisch, neu zuerst). */
  altNeuDisplay(an?: { alt?: string; neu?: string }): string {
    if (!an) return '';
    return [an.neu, an.alt].filter(v => !!v).join(' / ');
  }

  mosaikDisplay(): string {
    const m = this.bp.extendedHeader?.mosaikBereich;
    if (!m) return '';
    return [m.nummer, m.name, m.haupttyp].filter(v => !!v).join(' · ');
  }

  bodenformString(which: string) {
		return bodenformString(this.bp.header.bodenform, which);
  }

  qualityString(quality: number) {
    const e = this.ds.getHardcoded('Qualitätsstufen').find((entry: any) => entry.id === quality);
    return e ? e.name : "unbekannt";
  }

  hauptbodenform = () => this.bodenformString('hbf');
  feinbodenform = () => this.bodenformString('fbf');

  projekteString(): string {
    const projects = this.bp.header.projects;
    if (!projects || projects.length === 0) return 'Kein Projekt zugeordnet';
    return projects.map(p => p.name).join(', ');
  }

  /**
   * Erstellt eine verbale Zusammenfassung der Relief-Informationen
   * z.B. "Ebene in Horstlage, doppelt konvex; leicht bewegt"
   * Zeigt auch Teilinformationen an, wenn nicht alle Felder ausgefüllt sind.
   */
  reliefBeschreibung(): string {
    const relief = this.bp?.extendedHeader?.relief;
    if (!relief) return '';

    const parts: string[] = [];

    // Teil 1: Neigung + Hangteil + Einbindung/Hanganschluss
    let neigungTeil = '';

    if (relief.neigungsstufe) {
      neigungTeil = relief.neigungsstufe;
      // Bei Hängen: Hangteil anhängen
      if (relief.hangteil) {
        // z.B. "schwächer geneigter Lehn-Oberhang"
        neigungTeil = neigungTeil.replace(/hang$/, `-${relief.hangteil}`);
      }
    } else if (relief.hangteil) {
      // Nur Hangteil ohne Neigungsstufe
      neigungTeil = relief.hangteil;
    }

    // Einbindung/Lage anhängen (auch ohne Neigungsstufe)
    if (relief.einbindungEbene) {
      if (neigungTeil) {
        neigungTeil += ` in ${relief.einbindungEbene}`;
      } else {
        neigungTeil = relief.einbindungEbene;
      }
    } else if (relief.hanganschluss) {
      let anschlussTeil = relief.hanganschluss;
      if (relief.hoehenspanne) {
        anschlussTeil += ` (${relief.hoehenspanne} m)`;
      }
      if (neigungTeil) {
        neigungTeil += ` in ${anschlussTeil}`;
      } else {
        neigungTeil = anschlussTeil;
      }
    } else if (relief.hoehenspanne && neigungTeil) {
      // Höhenspanne ohne Hanganschluss aber mit Neigung
      neigungTeil += ` (${relief.hoehenspanne} m)`;
    }

    if (neigungTeil) {
      parts.push(neigungTeil);
    }

    // Teil 2: Wölbung (verbal)
    if (relief.woelbung) {
      const woelbungMap: Record<string, string> = {
        'xx': 'doppelt konvex',
        'xg': 'konvex-gestreckt',
        'xv': 'konvex-konkav',
        'gx': 'gestreckt-konvex',
        'gg': 'doppelt gestreckt',
        'gv': 'gestreckt-konkav',
        'vx': 'konkav-konvex',
        'vg': 'konkav-gestreckt',
        'vv': 'doppelt konkav'
      };
      const woelbungText = woelbungMap[relief.woelbung] || relief.woelbung;
      parts.push(woelbungText);
    }

    // Teil 3: Hangrichtung
    if (relief.hangrichtung) {
      parts.push(`${relief.hangrichtung}-gerichtet`);
    }

    // Erster Teil zusammenfügen (Komma-getrennt)
    let result = parts.join(', ');

    // Teil 4: Innere Rauhheit (nach Semikolon)
    if (relief.innereRauhheit) {
      if (result) {
        result += '; ' + relief.innereRauhheit;
      } else {
        result = relief.innereRauhheit;
      }
    }

    // Fallback: Wenn keine neuen Felder gesetzt sind, Legacy-Relief anzeigen
    if (!result) {
      result = [relief.relief, relief.reliefUmgebung].filter(v => !!v).join(' / ');
    }

    return result;
  }

}
